<template>
  <!-- // Mobile menu list items -->
  <template v-if="isMobile">
    <DefineMobileMenuLink v-slot="{ item, index }">
      <UiButton
        :class="
          cn(
            'text-primary h-auto whitespace-pre rounded-none px-5 py-0 text-base hover:bg-neutral-100 [&.active]:font-semibold'
          )
        "
        :id="genClickId('menu_link', index)"
        variant="list-item"
        :to="item?.to"
        @click="closeMenu"
      >
        {{ item.label }}
      </UiButton>
    </DefineMobileMenuLink>

    <template v-for="(item, index) in mappedMenuItems" :key="index">
      <div class="border-t py-2 first:border-t-0" v-if="item.links?.length">
        <div class="flex w-full items-center gap-1 px-3 py-3">
          <span class="text-muted-foreground text-sm">{{ item.label }}</span>

          <div class="bg-muted text-xxs/4 rounded-full px-1.5 font-medium text-black/70" v-if="item.badge">
            {{ item.badge }}
          </div>
        </div>

        <ul v-if="item.links?.length">
          <li v-for="(link, linkIndex) in item.links" :key="linkIndex">
            <ReuseMobileMenuLink :item="link" :index="linkIndex" />
          </li>
        </ul>
      </div>

      <div v-else :class="cn('border-border border-t py-2', { 'border-t-0 p-0': !item.links?.length })">
        <ReuseMobileMenuLink :item="item" :index="index" />
      </div>
    </template>
  </template>

  <!-- // Desktop 'Product' menu popover -->
  <template v-if="!isMobile">
    <template v-for="(item, index) in mappedMenuItems" :key="index">
      <UiButton
        v-if="item.to"
        :class="
          cn(
            'text-primary hover:font-normalwwww whitespace-pre px-0 py-0 leading-none hover:text-black/50 [&.active]:font-semibold'
          )
        "
        :id="genClickId('menu_link', index)"
        variant="list-item"
        :to="item?.to"
        @click="closeMenu"
      >
        {{ item.label }}
      </UiButton>

      <UiNavigationMenu
        v-else-if="item.links?.length"
        :classes="{
          itemLink: 'px-5 leading-6',
          trigger:
            'md:px-xs text-primary whitespace-pre px-0 py-0 leading-none hover:bg-transparent hover:font-normal focus:bg-transparent',
        }"
        :items="item.links"
      >
        <template #trigger>
          <UiButton
            :class="
              cn(
                'md:px-xs text-primary hover:background-none whitespace-pre bg-transparent px-0 py-0 text-base font-normal leading-none hover:text-black/50 data-[state=open]:bg-transparent'
              )
            "
            variant="list-item"
          >
            {{ item.label }}
          </UiButton>
        </template>
      </UiNavigationMenu>
    </template>
  </template>
</template>

<script setup lang="ts">
import { tempReplaceLinkIfOwned } from "@finq/app-base/utils/common/product-utils"

import { type PlanCode, PricingProductEnum } from "@finq/pricing/types"

type MenuItem = {
  label: string
  badge?: string
  links?: MenuItem[]
  to?: string
}

type ProductNavigationMenuLink = {
  id: keyof typeof PricingProductEnum
  screenPath: string
  label: string
  planCode: PlanCode
  isNew?: boolean
}

type MappedProductNavigationMenuLink = Omit<ProductNavigationMenuLink, "id"> & {
  id: string
  to?: string
}

const { isMobile } = useDisplay()
const emit = defineEmits<{ (e: "menu:close"): void }>()
const { t } = useI18n({ useScope: "local" })
const { tmsafe } = useI18nUtils({ useScope: "local" })

const showMenu = ref<boolean>(false)
const user = useUser()
const router = useRouter()

const [DefineMobileMenuLink, ReuseMobileMenuLink] = createReusableTemplate<{
  item: MenuItem
  index: number
}>({ inheritAttrs: false })

const menuItems = computed(() => tmsafe<MenuItem[]>("items"))
const mappedMenuItems = computed(() =>
  menuItems.value.map((item) => ({
    ...item,
    id: genClickId("menu_link", item.label),
    links: item.links?.map((link) => ({
      ...link,
      id: genClickId("menu_link", link.label),
      to: link.to || "",
    })),
  }))
)

const isProductActive = (
  product: MappedProductNavigationMenuLink | MappedProductNavigationMenuLink[],
  index?: number
) => {
  if (product && Array.isArray(product)) {
    if (!isNullOrUndefined(index) && typeof index === "number") {
      return router.currentRoute.value.path.includes(product[index].screenPath)
    }

    return product.some((p) => router.currentRoute.value.path.includes(p.screenPath))
  }

  return router.currentRoute.value.path.includes(product?.screenPath)
}

const closeMenu = () => {
  showMenu.value = false
  emit("menu:close")
}
</script>

<i18n lang="json">
{
  "en": {
    "items": [
      {
        "label": "Our ETFs",
        "to": "/etf"
      },
      {
        "label": "About us",
        "to": "/about"
      },
      { "label": "Newsroom", "to": "https://press.finqai.com" }
    ]
  },
  "he": {}
}
</i18n>
